import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { values } from 'ramda'

import {
  FundSection,
  RefillStep,
  RefillTarget,
  RefillType,
  isFundSection,
  isRefillStep,
  isRefillTarget,
  isRefillType,
} from 'app/dataTypes/acquiring/settings'

import {
  Refill,
  BannerContainer,
  getIsReturnLinkAvailable,
  getIsNoSpaceForBanner,
} from 'app/components/Fund/Refill'

import {
  useAcquiringSettingsLoading,
  useAcquiringSettingsAvailability,
  useAcquiringSettingsRefillStyle,
} from 'app/dataProviders/acquiring/settings/hooks'

import Transfer from 'app/components/Fund/Transfer'
import { useTransferAvailability, useTransferLoading } from 'app/dataProviders/transfer/hooks'
import getRegAvailable from 'app/selectors/getRegAvailable'

import FundNavigation from '../../_common/FundNavigation'

type Props = {
  params: {
    id?: string
    type?: string
    step?: string
    target?: string
    order?: string
    schoolType?: string
    targetId?: string
  }
  router: {
    replace: (pathname: string) => void
    push: (pathname: string) => void
  }
}

const FundPage = ({ params, router }: Props) => {
  const { id, type, target, order, schoolType, step, targetId = null } = params
  const { push } = router
  const baseURL = `/apps/${id}/cafeteria/${schoolType}/fund`

  const fundSection = isFundSection(type) ? (type as FundSection) : null
  const refillStep = isRefillStep(step) ? (step as RefillStep) : null
  const refillTarget = isRefillTarget(target) ? (target as RefillTarget) : null
  const refillType = isRefillType(order) ? (order as RefillType) : null

  const createHomePath = useCallback(
    (op: 'refill' | 'transfer') => {
      // apps/:id/cafeteria/schoolType:/fund/:type(fundSection)/:target(refillTarget)/:order(refillType)/:step/:targetId
      if (op === 'refill') {
        return `${baseURL}/${FundSection.Refill}`
      }
      if (op === 'transfer') {
        return `${baseURL}/${FundSection.Transfer}`
      }
      return '' as never
    },
    [baseURL],
  )

  const redirectToDefault = useCallback(() => {
    push(`${baseURL}/${FundSection.Refill}`)
  }, [push, baseURL])

  const handleChangeStep = useCallback(
    ({
      step: s,
      target: trg = refillTarget,
      type: tp = refillType,
      targetID: trgID = targetId || undefined,
    }: {
      step: number
      target?: RefillTarget | null
      type?: RefillType | null
      targetID?: string
    }) => {
      const stepValue = [null, ...values(RefillStep)][s]

      if (trg && tp && stepValue) {
        const pathTo = `${baseURL}/${FundSection.Refill}/${trg}/${tp}/${stepValue}`
        if (trgID) {
          push(`${pathTo}/${trgID}`)
          return
        }
        push(pathTo)
        return
      }
      redirectToDefault()
    },
    [baseURL, push, refillTarget, refillType, redirectToDefault, targetId],
  )

  useEffect(() => {
    if (!fundSection) redirectToDefault()
  }, [fundSection]) // eslint-disable-line react-hooks/exhaustive-deps

  // const isTransferAvailable = useSelector(transferAvailable) // isParent + has students with enabled income (много селекторов)
  // частный случай со столовой С.-Петербурга в кабинете родителей
  const regAvailable = useSelector(getRegAvailable)
  // скрыть пункт навигации "Перевести" в подразделе "Пополнить"
  // const hideTransferNavItem = type === FundSection.Refill
  // не отображать пополнение школы на вкладке лагеря
  const hideReplenishChild = schoolType === 'camp'
  // не отображать пополнение лагеря на вкладке школы
  const hideReplenishChildCamp = schoolType === 'general'

  // возможность пополнения
  const refillAvailable = useAcquiringSettingsAvailability()
  const loadingRefill = useAcquiringSettingsLoading()

  // стиль пополнения
  const refillStyle = useAcquiringSettingsRefillStyle(refillType, targetId)

  const transferAvailable = useTransferAvailability()
  // скрыть пункт навигации "Перевести" в подразделе "Пополнить"
  const showTransfer = transferAvailable && type !== FundSection.Refill
  const loadingTransfer = useTransferLoading()

  const refillHomeURL = createHomePath('refill')
  const successURL = `${window.location.origin}${baseURL}/success`
  const failureURL = `${window.location.origin}${baseURL}/fail`
  const transferHomeURL = createHomePath('transfer')

  if (!fundSection) return null

  return (
    <>
      <FundNavigation
        activeFundSection={fundSection}
        showRefillLink={refillAvailable}
        refillLink={refillHomeURL}
        showTransferLink={showTransfer}
        transferLink={transferHomeURL}
        showReturnLink={getIsReturnLinkAvailable(refillStep, refillStyle)}
        returnLink={refillHomeURL}
        loading={loadingRefill || loadingTransfer}
      />

      {(fundSection === FundSection.Refill ||
        fundSection === FundSection.Success ||
        fundSection === FundSection.Fail) &&
        refillAvailable && (
          <BannerContainer
            bannerPlaceID="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
            hide={getIsNoSpaceForBanner(refillStep, refillStyle)}
          >
            <Refill
              cafeteriaOwnAccountRefillDisable
              payForAlerts={regAvailable}
              showSuccess={fundSection === FundSection.Success}
              showFail={fundSection === FundSection.Fail}
              refillStep={refillStep}
              refillTarget={refillTarget}
              refillType={refillType}
              targetID={targetId}
              homeURL={refillHomeURL}
              successURL={successURL}
              failureURL={failureURL}
              hideReplenishChild={hideReplenishChild}
              hideReplenishChildCamp={hideReplenishChildCamp}
              onChangeStep={handleChangeStep}
            />
          </BannerContainer>
        )}

      {fundSection === FundSection.Transfer && transferAvailable && <Transfer />}
    </>
  )
}

export default FundPage
